<template>
  <div>
    <Toast />
    <HeaderBreadcrumbs
      :items="[
        { label: 'Publisher', to: '/vendor' },
        { label: 'Add New' }
      ]"
      title="New Publisher"
    />

    <div class="flex items-center px-4 py-3 my-3 space-x-4 bg-white rounded-lg shadow-sm tab-header">
      <div :class="['tab-item flex items-center', { 'text-blue-500 font-semibold': currentTab === 1 }]">
        <span :class="['font-bold rounded-full border p-1.5 mr-2 text-center w-9 h-9', { 'border-custom': currentTab === 1 }]">1</span>
        Publisher Details
      </div>
      <span class="text-gray-400">»</span>
      <div :class="['tab-item flex items-center', { 'text-blue-500 font-semibold': currentTab === 2 }]">
        <span :class="['font-bold rounded-full border p-1.5 mr-2 text-center w-9 h-9', { 'border-custom': currentTab === 2 }]">2</span>
        Publisher Agreement
      </div>
      <span class="text-gray-400">»</span>
      <div :class="['tab-item flex items-center', { 'text-blue-500 font-semibold': currentTab === 3 }]">
        <span :class="['font-bold rounded-full border p-1.5 mr-2 text-center w-9 h-9', { 'border-custom': currentTab === 3 }]">3</span>
        Publisher Report Format
      </div>
    </div>

    <div class="flex-col w-full mb-3 bg-white rounded-lg">

      <div v-if="currentTab === 1" class="flex items-center justify-between w-full px-4 py-3 mt-3 rounded-lg ">
        <div class="flex flex-col w-1/2">
          <FormLabel htmlFor="vendor-name">
            Publisher Name
          </FormLabel>

          <TextInput
            type="text"
            id="vendor-name"
            placeholder="Enter Publisher Name"
            :variant="isSubmitted && isNameError ? 'error' : 'default'"
            v-model="form.name"
          />

          <div v-if="isSubmitted && isNameError" class="mt-1 text-sm text-red-500">Must be filled.</div>

          <FormLabel htmlFor="vendor-name" className="mt-3">
            Publisher Image (Optional)
          </FormLabel>

          <div>
            <label for="input-image" class="rounded-lg cursor-pointer">
              <img
                v-if="form.preview"
                :src="form.preview"
                class="object-contain rounded-lg img-fluid"
                style="height: 144px; width: 144px;"
                height="144"
                width="144"
              />
              <div
                v-else
                :class="['flex flex-col items-center bg-gray-50 border-2 px-1 border-gray-200 hover:border-gray-400 border-dashed rounded-lg gap-2']"
                style="height: 144px; width: 144px; padding-top: 37px;"
              >
                <FileCirlclePlusFilled />
                <div class="text-xs font-semibold text-gray-500">
                  Upload Image
                </div>
                <div class="text-xs font-normal text-gray-500">
                  Max 2MB
                </div>
              </div>
            </label>
          </div>

          <input
            id="input-image"
            type="file"
            accept="image/*"
            class="hidden"
            @change="previewImage"
          />

          <FormLabel htmlFor="organization" className="mt-3">
            Organization
          </FormLabel>

          <multiselect
            v-model="form.organization"
            :key="`organization-${currentTab}`"
            label="name"
            track-by="name"
            placeholder="Select Organization"
            open-direction="bottom"
            class="box-border custom-multiselect"
            :class="isSubmitted && isOrganizationError ? 'error' : ''"
            :options="organizationOptions"
            :searchable="true"
            :loading="isLoadingOrganization"
            :close-on-select="true"
            @search-change="handleSearchOrganization"
          >
            <span slot="noResult">
              Oops! No elements found. Consider changing the search query.
            </span>
          </multiselect>

          <div v-if="isSubmitted && isOrganizationError" class="mt-1 text-sm text-red-500">Must be filled.</div>

          <FormLabel htmlFor="isExternal" className="mt-3">
            Publisher Type
          </FormLabel>

          <SelectRadio
            id="isExternal"
            :key="`isExternal-${currentTab}`"
            defaultText="Select Vendor Type"
            v-model="form.isExternal"
            :options="vendorTypeOptions"
            :isError="isSubmitted && isExternalError"
          />

          <div v-if="isSubmitted && isExternalError" class="mt-1 text-sm text-red-500">Must be filled.</div>

          <FormLabel htmlFor="vendorSource" className="mt-3">
            Publisher Source
          </FormLabel>

          <SelectRadio
            id="vendorSource"
            defaultText="Select Vendor Source"
            v-model="form.vendorSource"
            :options="vendorSourceOptions"
            :isError="isSubmitted && isVendorSourceError"
          />

          <div v-if="isSubmitted && isVendorSourceError" class="mt-1 text-sm text-red-500">Must be filled.</div>

          <FormLabel htmlFor="vendorSource" className="mt-3">
            Publisher Content
          </FormLabel>

          <SelectCheckbox
            id="vendorContent"
            defaultText="Select Vendor Content"
            v-model="form.contentTypes"
            :options="contentTypeOptions"
             :isError="isSubmitted && isVendorContentError"
          />
      <div v-if="isSubmitted && isVendorContentError" class="mt-1 text-sm text-red-500">Must be filled.</div>

          <FormLabel htmlFor="status" className="mt-3">
            Approval Status
          </FormLabel>

          <SelectRadio
            id="status"
            defaultText="Select Approval Status"
            v-model="form.status"
            :options="statusOptions"
            :isError="isSubmitted && isStatusError"
          />

          <div v-if="isSubmitted && isStatusError" class="mt-1 text-sm text-red-500">Must be filled.</div>

          <FormLabel htmlFor="vendorId" className="mt-3">
            Publisher ID from AX
          </FormLabel>

          <TextInput
            type="text"
            id="vendorId"
            placeholder="Enter Publisher ID from AX"
            :variant="isSubmitted && isVendorIdAxError ? 'error' : 'default'"
            v-model="form.vendorIdAx"
          />

          <div v-if="isSubmitted && isVendorIdAxError" class="mt-1 text-sm text-red-500">Must be filled.</div>

          <FormLabel htmlFor="description" className="mt-3">
            Description (Optional)
          </FormLabel>

          <TextArea
            id="description"
            rows="7"
            placeholder="Enter something..."
            v-model="form.description"
          />

          <FormLabel htmlFor="status" className="mt-3">
            Status
          </FormLabel>

          <GmSwitch id="status" v-model="form.isActive"/>

          <div class="flex gap-5 mt-2">
            <Button @click="validateAndNext">
              Next
            </Button>

            <Button
              variant="outlined"
              color="alternative"
              @click="prevTab"
            >
              Cancel
            </Button>
          </div>

        </div>

      </div>
      <div v-if="currentTab === 2" class="flex items-center justify-between w-full px-4 py-3 mt-3 rounded-lg ">
        <div class="flex flex-col w-1/2">
          <FormLabel htmlFor="currency">
            Payment Currency
          </FormLabel>

          <SelectRadio
            id="currency"
            :key="`currency-${currentTab}`"
            defaultText="Select Payment Currency"
            v-model="form.vACurrency"
            :options="currencyOptions"
            :isError="isSubmitted && isVACurrencyError"
          />

          <div v-if="isSubmitted && isVACurrencyError" class="mt-1 text-sm text-red-500">Must be filled.</div>

          <FormLabel htmlFor="revenue" class="mt-3">
            Revenue Share
          </FormLabel>

          <multiselect
            v-model="form.vARevenue"
            :key="`revenue-${currentTab}`"
            label="name"
            track-by="name"
            placeholder="Select Revenue Share"
            open-direction="bottom"
            class="box-border custom-multiselect"
            :class="isSubmitted && isVARevenueError ? 'error' : ''"
            :options="revenueItems"
            :searchable="true"
            :loading="isLoadingSearchRevenue"
            :close-on-select="true"
            @search-change="findRevenue"
            @select="selectRevenue"
          >
            <span slot="noResult">
              Oops! No elements found. Consider changing the search query.
            </span>
          </multiselect>

          <div v-if="isSubmitted && isVARevenueError" class="mt-1 text-sm text-red-500">Must be filled.</div>

          <DatetimeCustom
            :timeOnlyHours="true"
            class="mt-3"
            :label="'Start Date'"
            v-model="form.vAStartDate"
          />

          <div v-if="isSubmitted && isVAStartDateError" class="mt-1 text-sm text-red-500">Must be filled.</div>

          <DatetimeCustom
            :timeOnlyHours="true"
            class="mt-3"
            :label="'End Date'"
            v-model="form.vAEndDate"
          />

          <div v-if="isSubmitted && isVAEndDateError" class="mt-1 text-sm text-red-500">Must be filled.</div>

          <FormLabel htmlFor="status" className="mt-3">
            Status
          </FormLabel>

          <GmSwitch id="status" v-model="form.vAIsActive"/>

          <FormLabel className="mt-3" disabled>
            Default Fixed Amount
          </FormLabel>

          <TextInput
            type="text"
            id="defaultFixed"
            class="text-gray-400"
            disabled
            :value="defaultFixed || '-'"
          />

          <FormLabel className="mt-3" disabled>
            Default Percentage Amount
          </FormLabel>

          <TextInput
            type="text"
            id="defaultPercentage"
            class="text-gray-400"
            disabled
            :value="defaultPercentage || '-'"
          />

          <div class="flex gap-5 mt-4">
            <Button @click="validateAndNext">
              Next
            </Button>

            <Button
              variant="outlined"
              color="alternative"
              @click="prevTab"
            >
              Back
            </Button>
          </div>

        </div>
      </div>

      <div v-if="currentTab === 3" class="flex items-center justify-between w-full px-4 py-3 mt-3 rounded-lg ">
        <div class="flex flex-col w-1/2">

          <FormLabel htmlFor="contentType">
            Content Type
          </FormLabel>

          <div class="flex gap-6">
            <b-form-checkbox v-model="form.vRFIsSingle" :unchecked-value="false">
              Single
            </b-form-checkbox>

            <b-form-checkbox v-model="form.vRFIsBundle" :unchecked-value="false">
              Bundle
            </b-form-checkbox>

            <b-form-checkbox v-model="form.vRFIsSubscription" :unchecked-value="false">
              Subscription
            </b-form-checkbox>

            <b-form-checkbox v-model="form.vRFIsBuffet" :unchecked-value="false">
              Buffet
            </b-form-checkbox>
          </div>

          <FormLabel htmlFor="reason" class="mt-3">
            Reason (Optional)
          </FormLabel>

          <TextArea
            id="reason"
            rows="7"
            placeholder="Type reason here..."
            v-model="form.vRFReason"
          />

          <FormLabel htmlFor="status" class="mt-3">
            Status
          </FormLabel>

          <GmSwitch id="status" v-model="form.vRFIsActive"/>


          <div class="flex gap-5 mt-4">
            <Button @click="createVendor">
              Save
            </Button>

            <Button
              variant="outlined"
              color="alternative"
              @click="prevTab"
            >
              Back
            </Button>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import moment from 'moment';
import HeaderBreadcrumbs from '@/components/HeaderBreadcrumbs';
import FormLabel from '@/components/ui/FormLabel';
import GmSwitch from '@/components/ui/Switch';
import TextInput from '@/components/ui/TextInput';
import TextArea from '@/components/ui/TextArea';
import Button from '@/components/ui/Button';
import SelectRadio from '@/components/ui/SelectRadio';
import SelectCheckbox from '@/components/ui/SelectCheckbox';
import FileCirlclePlusFilled from '@/components/icons/FileCirclePlusFilled';
import DatetimeCustom from '@/components/form/Datetime';

import API from '@/plugins/http';
import Toast from '@/components/form/Toast';
import { EventBus } from '@/event-bus';

import constant from '@/store/constant';

const paymentCurrencies = Object.entries(constant.PUBLISHER_PAYMENT_CURRENCY).map(
  (val) => ({
    text: val[0],
    value: val[1],
  }),
);

export default {
  name: 'vendorNewCreate',
  components: {
    HeaderBreadcrumbs,
    FormLabel,
    GmSwitch,
    TextInput,
    TextArea,
    Button,
    FileCirlclePlusFilled,
    DatetimeCustom,
    SelectRadio,
    SelectCheckbox,
    Toast
  },
  data() {
    return {
      currentTab: 1,
      form: {
        name: '',
        preview: '',
        image: '',
        organization: null,
        status: null,
        isExternal: null,
        vendorSource: null,
        vendorIdAx: '',
        contentTypes: [],
        description: '',
        isActive: false,

        vACurrency: null,
        vARevenue: null,
        vAStartDate: moment().toString(),
        vAEndDate: moment().toString(),
        vAIsActive: false,

        vRFIsActive: false,
        vRFReason: '',
        vRFIsSingle: false,
        vRFIsBundle: false,
        vRFIsSubscription: false,
        vRFIsBuffet: false,
      },

      organizationOptions: [],
      isLoadingOrganization: false,

      contentTypeOptions: [
        { value: 'isContentBooks', text: 'Books' },
        { value: 'isContentMagazines', text: 'Magazines' },
        { value: 'isContentNewspapers', text: 'Newspapers' }
      ],

      vendorTypeOptions: [
        { value: false, text: 'Internal' },
        { value: true, text: 'External' }
      ],
      vendorSourceOptions: [
        { value: 'Gramedia Digital', text: 'Gramedia Digital' },
        { value: 'SmartLib Client', text: 'SmartLib Client', },
      ],
      statusOptions: [
        // { value: null, text: 'Select Approval Status' },
        { value: '1', text: 'New' },
        {
          value: '2',
          text: 'Waiting for Review',
        },
        { value: '3', text: 'In Review' },
        { value: '4', text: 'Rejected' },
        { value: '5', text: 'Approved' },
        { value: '6', text: 'Clear' },
        { value: '7', text: 'Banned' },
      ],

      currencyOptions: [
        ...paymentCurrencies,
      ],

      isLoadingSearchRevenue: false,
      revenueItems: [],

      defaultFixed: null,
      defaultPercentage: null,
      isLoadingCreateVendor: false,
      isSubmitted: false,
    }
  },
  computed: {
    ...mapState({
      itemsRevenueShares: ({ revenueshares }) => revenueshares.items,
    }),
    isNameError() {
      return !this.form.name.trim();
    },
    isOrganizationError() {
      return !this.form.organization;
    },
    isExternalError() {
      return this.form.isExternal === null;
    },
    isVendorSourceError() {
      return !this.form.vendorSource;
    },
 isVendorContentError() {
  return !this.form.contentTypes || this.form.contentTypes.length === 0;
},
    isStatusError() {
      return !this.form.status;
    },
    isVendorIdAxError() {
      return !this.form.vendorIdAx || !this.form.vendorIdAx.trim();
    },
    isVACurrencyError() {
      return !this.form.vACurrency;
    },
    isVARevenueError() {
      return !this.form.vARevenue;
    },
    isVAStartDateError() {
      return  !this.form.vAStartDate;
    },
    isVAEndDateError() {
      return !this.form.vAEndDate;
    }
  },
  methods: {
    ...mapActions('organization', ['searchOrganization']),
    ...mapActions('vendor_revenue', [
      'findVendorRevenueByID',
      'findVendorRevenue',
      'createVendorRevenue'
    ]),
    previewImage(event) {
      const input = event.target;
      if (input.files) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.form.preview = e.target.result;
        }
        this.form.image = input.files[0];
        reader.readAsDataURL(input.files[0])
      }
    },
    handleSearchOrganization(keyword) {
      if (keyword.length > 2) {
        this.isLoadingOrganization = true;
        const payload = {
          search: keyword
        }
        this.searchOrganization(payload)
          .then((resp) => {
            this.organizationOptions = resp.data.data;
            this.isLoadingOrganization = false;
          })
          .catch((err) => {
            this.isLoadingOrganization = false;
            console.error(err)
          })
      }
    },
    validateAndNext() {
      if (this.currentTab >= 3) {
        return
      }

      if (this.currentTab === 1) {
        if (
          !this.isNameError &&
          !this.isOrganizationError &&
          !this.isExternalError &&
          !this.isVendorSourceError &&
          !this.isVendorContentError &&
          !this.isStatusError &&
          !this.isVendorIdAxError
        ) {
          this.isSubmitted = false;
          this.currentTab++;
        } else {
          this.isSubmitted = true;
        }
      } else if (this.currentTab === 2) {
        if (
          !this.isVACurrencyError &&
          !this.isVARevenueError &&
          !this.isVAStartDateError &&
          !this.isVAEndDateError
        ) {
          this.isSubmitted = false;
          this.currentTab++;
        } else {
          this.isSubmitted = true;
        }
      }
    },

    prevTab() {
      if (this.currentTab > 1) {
        this.currentTab--;
      } else {
        this.$router.replace('/vendor')
      }
    },

    findRevenue(keyword) {
      if (keyword.length > 2) {
        this.isLoadingSearchRevenue = true;
        const payload = {
          q: keyword,
        };
        this.findVendorRevenue(payload)
          .then((resp) => {
            this.revenueItems = resp.data.data;
            this.isLoadingSearchRevenue = false;
          })
          .catch((err) => {
            this.isLoadingSearchRevenue = false;
            console.log(err);
          });
      }
    },

    selectRevenue(data) {
      this.findVendorRevenueByID(data).then((res) => {
        const { default_fixed_amount, default_percentage_amount } = res.data.data;
        this.defaultFixed = default_fixed_amount;
        this.defaultPercentage = default_percentage_amount;
      });
    },

    async createVendor() {
      this.isLoadingCreateVendor = true;

      const formData = new FormData();
      formData.append('file', this.form.image);
      formData.append('vendor_details', JSON.stringify({
        name: this.form.name,
        organization: this.form.organization.id,
        status: this.form.status,
        vendor_id_ax: this.form.vendorIdAx,
        description: this.form.description,
        is_active: this.form.isActive,
        is_external: this.form.isExternal,
        is_content_books: this.form.contentTypes.includes('isContentBooks'),
        is_content_magazines: this.form.contentTypes.includes('isContentMagazines'),
        is_content_newspapers: this.form.contentTypes.includes('isContentNewspapers'),
        vendor_source: this.form.vendorSource
      }));
      formData.append('vendor_agreement', JSON.stringify({
        payment_currency: this.form.vACurrency,
        revenue_id: this.form.vARevenue.id,
        valid_from: this.form.vAStartDate,
        valid_to: this.form.vAEndDate,
        is_active_agreement: this.form.vAIsActive
      }));
      formData.append('vendor_report_format', JSON.stringify({
        status_report_format: this.form.vRFReason,
        is_active_report_format: this.form.vRFIsActive,
        single: this.form.vRFIsSingle,
        subscription: this.form.vRFIsSubscription,
        bundle: this.form.vRFIsBundle,
        buffet: this.form.vRFIsBuffet
      }));

      try {
        await API.post('/vendor/create/v2', formData)
          .then((res) => {
            if (res.status === 201) {
              this.$router.push('/vendor');
              EventBus.$emit('show-toast', {
                message: 'New vendor has been added.',
                type: 'success',
              });
            }
          })
      } catch (error) {
        console.error(error);
        
        // Check if the error response contains the vendor name already exists message
        const errorMessage = error.response?.data?.data?.message;
        let toastMessage = 'Failed To Create New Vendor';
        
        if (errorMessage === 'Vendor Name Is Already Exist') {
          toastMessage = 'Vendor Name is Already Exist';
        }
        
        EventBus.$emit('show-toast', {
          message: toastMessage,
          type: 'error',
        });
      } finally {
        this.isLoadingCreateVendor = false;
      }
    }
  },
  watch: {
  }
}
</script>
